import { RADIUS_OPTIONS, type Radius } from './embedConfig'
import type { PublicEvent } from './publicEvent'

export type Coordinates = { lat: number; lng: number }

export type FoundPlace = Coordinates & { name: string }

export type LocatedEvent = { event: PublicEvent; distance: number }

export type NearbyEvents = {
  place: string
  radius: number
  nearby: LocatedEvent[]
  nearest: LocatedEvent | null
  widerRadius: Radius | null
}

// Google's own spherical Earth radius, so distances match Maps' computeDistanceBetween.
const EARTH_RADIUS_KM = 6378.137

const radians = (degrees: number) => (degrees * Math.PI) / 180

export function distanceKm(from: Coordinates, to: Coordinates) {
  const h =
    Math.sin(radians(to.lat - from.lat) / 2) ** 2 +
    Math.cos(radians(from.lat)) * Math.cos(radians(to.lat)) * Math.sin(radians(to.lng - from.lng) / 2) ** 2
  return EARTH_RADIUS_KM * 2 * Math.asin(Math.sqrt(h))
}

export function eventsNear({
  events,
  place,
  radius,
}: {
  events: PublicEvent[]
  place: FoundPlace
  radius: number
}): NearbyEvents {
  const located = events
    .flatMap((event) => {
      const { lat, lng } = event.venue
      return lat === null || lng === null ? [] : [{ event, distance: distanceKm(place, { lat, lng }) }]
    })
    .sort((a, b) => a.distance - b.distance)
  const nearest = located[0] ?? null

  return {
    place: place.name,
    radius,
    nearby: located.filter(({ distance }) => distance <= radius),
    nearest,
    widerRadius: nearest
      ? (RADIUS_OPTIONS.find((option) => option > radius && nearest.distance <= option) ?? null)
      : null,
  }
}

export function formatDistance(km: number, locale: string) {
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: 'kilometer',
    maximumFractionDigits: km < 10 ? 1 : 0,
  }).format(km)
}
